import {
  Box,
  Divider,
  HStack,
  Icon,
  Image,
  Text,
  VStack,
} from "@chakra-ui/react";
import { FaFacebook, FaInstagram } from "react-icons/fa";
import type { CampaignIntent, DisplayMarketingAd } from "common/interfaces/KYDMarketing";
import {
  AdFormat,
  PublisherPlatforms,
  RatioType,
} from "common/interfaces/KYDMarketing";
import type {
  FacebookPage,
  InstagramAccount,
} from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/types";
import { CampaignDetailLink } from "./CampaignDetailLink";
import { PerformanceTierBadge } from "./PerformanceTierBadge";
import { getMetric, formatRoas, getInsightItems } from "../adSetCardUtils";
import { AdInsightRow } from "./AdInsightRow";
import { Button } from "./Button";
import MetricsCard from "./MetricsCard";
import { RatioLabel } from "./RatioLabel";
import { MetaSyncStatusLabel } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/components/StatusBadge";
import { normalizeCreativeIdentityForWire } from "@common/utils/guidedEditAdSerializers";

function getAdCreativeType(ad: DisplayMarketingAd): "Poster" | "Video" {
  const hasVideo = (ad.creative?.assets?.videos?.length ?? 0) > 0;
  const format = ad.creative?.ad_formats?.[0];
  if (format === AdFormat.SINGLE_VIDEO || hasVideo) return "Video";
  return "Poster";
}

export function AdDetailBlock({
  ad,
  onEditAdset,
  onEditAd,
  pages,
  instagramAccounts,
  intentsForAd = [],
  onApplyIntent,
  onIgnoreIntent,
  applyingIntentId,
  onOpenWizardReview,
  onPublishAd,
  onUnpublishAd,
  ignoringIntentId,
  isActivatingAd,
  isPausingAd,
  isApplyingChanges = false,
}: {
  ad: DisplayMarketingAd;
  onEditAdset: () => void;
  /** When set, Edit opens the wizard on this ad's creative form (advanced flow). */
  onEditAd?: (ad: DisplayMarketingAd) => void;
  pages: FacebookPage[];
  instagramAccounts: InstagramAccount[];
  intentsForAd?: CampaignIntent[];
  onApplyIntent?: (intent: CampaignIntent) => void | Promise<void>;
  onIgnoreIntent?: (intent: CampaignIntent) => void | Promise<void>;
  /** Intent id currently being applied (show loading on that link). */
  applyingIntentId?: string | null;
  /** Open the ads manager wizard on the review step (for "View" on frequency/neutral intents). */
  onOpenWizardReview?: () => void;
  /** Activate (publish) this ad. */
  onPublishAd?: () => void | Promise<void>;
  /** Pause (unpublish) this ad. */
  onUnpublishAd?: () => void | Promise<void>;
  /** Intent id currently being ignored (show "Ignoring…" on that link). */
  ignoringIntentId?: string | null;
  /** When true, show loading on the Activate button. */
  isActivatingAd?: boolean;
  /** When true, show loading on the Pause button. */
  isPausingAd?: boolean;
  /** Guided publish / focused save in progress — disable edit actions. */
  isApplyingChanges?: boolean;
}) {
  const creative = ad.creative;
  const assets = creative?.assets;
  const images = assets?.images ?? [];
  const videos = assets?.videos ?? [];
  const primaryText = assets?.bodies?.[0]?.text ?? "";
  const adMetrics = ad.metrics;
  const spend = getMetric(adMetrics, "display_spend");
  const roasRaw = getMetric(adMetrics, "display_kyd_roas");
  const roas = formatRoas(roasRaw);
  const ctr = getMetric(adMetrics, "display_ctr");
  const adStatus = (ad as { status?: string }).status;
  const creativeType = getAdCreativeType(ad);
  const creativeTitle = creative?.name?.trim() || creativeType;
  const links = assets?.links ?? [];

  const effectiveIdentity = normalizeCreativeIdentityForWire(creative?.identity);
  const facebookPageId =
    effectiveIdentity?.[PublisherPlatforms.facebook]?.page_id;
  const instagramActorId =
    effectiveIdentity?.[PublisherPlatforms.instagram]?.instagram_actor_id;
  const facebookPageName = facebookPageId
    ? pages.find((p) => p.id === facebookPageId)?.name
    : null;
  const instagramUsername = instagramActorId
    ? instagramAccounts.find((a) => a.id === instagramActorId)?.username
    : null;
  const trimmedIgUser = instagramUsername?.trim() ?? "";
  const instagramDisplayLabel =
    trimmedIgUser || (instagramActorId ? "Instagram account" : null);
  const hasIdentity = !!(facebookPageId || instagramActorId);

  const creativeItems: {
    src: string;
    type?: RatioType;
    file: string;
    isVideo?: boolean;
    videoSrc?: string;
  }[] = [];
  for (const img of images) {
    creativeItems.push({
      src: img.src,
      type: img.type,
      file: img.file_key ?? "file_name.png",
    });
  }
  for (const v of videos) {
    creativeItems.push({
      src: v.thumbnail_src ?? v.src,
      type: v.type,
      file: v.file_key ?? "file_name.mp4",
      isVideo: true,
      videoSrc: v.src,
    });
  }

  const adMetricsItems = [
    { id: "spend", label: "Spend", value: spend },
    { id: "roas", label: "ROAS", value: roas },
    { id: "ctr", label: "Avg. CTR", value: ctr },
  ];

  const insightItems = getInsightItems({
    entityType: "ad",
    entity: ad,
    intents: intentsForAd,
  });

  return (
    <VStack
      bg="white"
      borderRadius="2xl"
      borderWidth="1px"
      borderColor="gray.200"
      align="stretch"
      w="full"
      p={4}
      gap={2}
      boxShadow="0 2px 12px rgba(0,0,0,0.06)"
    >
      <HStack justify="space-between" align="center" mb={0} flexWrap="wrap">
        <HStack gap={2}>
          <Text fontWeight="600" color="gray.800" fontSize="sm">
            {creativeTitle}
          </Text>
          <HStack align="flex-start">
            <MetaSyncStatusLabel
              status={ad.status}
              hasExternalId={!!ad.external_id}
            />
            <PerformanceTierBadge performance={ad.performance} />
          </HStack>
        </HStack>
        <HStack gap={2} flexWrap="wrap">
          {onOpenWizardReview && (
            <Button onClick={onOpenWizardReview} isDisabled={isApplyingChanges}>
              Edit
            </Button>
          )}
          {adStatus === "ACTIVE" && onUnpublishAd && (
            <Button
              onClick={() => onUnpublishAd()}
              isLoading={isPausingAd}
              isDisabled={isPausingAd || isApplyingChanges}
            >
              Pause
            </Button>
          )}
          {adStatus === "PAUSED" && onPublishAd && (
            <Button
              bg="gray.800"
              color="white"
              borderColor="gray.800"
              _hover={{ bg: "gray.700", opacity: 0.9 }}
              onClick={() => onPublishAd()}
              isLoading={isActivatingAd}
              isDisabled={isActivatingAd || isApplyingChanges}
            >
              Resume
            </Button>
          )}
        </HStack>
      </HStack>

      <MetricsCard items={adMetricsItems}>
        {insightItems.length > 0 && (
          <VStack align="stretch" spacing={2} w="100%">
            {insightItems.map((item, index) => (
              <AdInsightRow
                key={item.intent.id}
                item={item}
                showDivider={index > 0}
                onOpenWizardReview={onOpenWizardReview}
                onApplyIntent={onApplyIntent}
                onIgnoreIntent={onIgnoreIntent}
                applyingIntentId={applyingIntentId}
                ignoringIntentId={ignoringIntentId}
                isApplyingChanges={isApplyingChanges}
              />
            ))}
          </VStack>
        )}
      </MetricsCard>

      {creativeItems.length > 0 ? (
        <HStack gap={2} w="full" align="stretch">
          {creativeItems.map((item, idx) => {
            return (
              <Box
                key={`${item.file}-${idx}`}
                bg="#FAFAFA"
                borderRadius="2xl"
                overflow="hidden"
                borderWidth="1px"
                borderStyle="dashed"
                borderColor="gray.300"
                p={3}
                flex={1}
                minW={0}
              >
                <VStack align="stretch" spacing={0} gap={0}>
                  <RatioLabel
                    type={item.type}
                    textAlign="center"
                    mb={3}
                    fontSize="xs"
                  />
                  <Box
                    h="150px"
                    aspectRatio={item.type === RatioType.square ? 1 : 9 / 16}
                    flexShrink={0}
                    borderRadius="4px"
                    borderWidth="1px"
                    borderColor="gray.200"
                    boxShadow="0 0 2px -1px #000, 0 4px 10px 0 rgba(74, 85, 104, 0.39);"
                    overflow="hidden"
                    alignSelf="center"
                  >
                    {item.isVideo && item.videoSrc ? (
                      <Box
                        as="video"
                        src={item.videoSrc}
                        poster={item.src}
                        muted
                        autoPlay
                        playsInline
                        controls
                        w="100%"
                        h="100%"
                        aspectRatio={
                          item.type === RatioType.square ? 1 : 9 / 16
                        }
                        objectFit="cover"
                        sx={{ display: "block" }}
                      />
                    ) : (
                      <Image
                        src={item.src}
                        alt=""
                        h="100%"
                        w="100%"
                        aspectRatio={
                          item.type === RatioType.square ? 1 : 9 / 16
                        }
                        objectFit="cover"
                      />
                    )}
                  </Box>
                </VStack>
              </Box>
            );
          })}
        </HStack>
      ) : (
        <Text
          fontSize="sm"
          color="gray.500"
          py={2}
          mb={primaryText || links.length > 0 ? 4 : 0}
        >
          No creatives
        </Text>
      )}

      {primaryText && (
        <>
          <Divider borderColor="gray.200" />
          <VStack px={4} align="stretch" w="full">
            <HStack justify="space-between" align="center">
              <Text
                fontSize="xs"
                color="gray.600"
                fontFamily="Work Sans"
                fontWeight="600"
                lineHeight="130%"
              >
                Primary Text
              </Text>
              <CampaignDetailLink
                fontSize="xs"
                onClick={
                  isApplyingChanges
                    ? undefined
                    : () => (onEditAd ? onEditAd(ad) : onEditAdset())
                }
                aria-disabled={isApplyingChanges}
                _disabled={{
                  color: "gray.400",
                  textDecoration: "none",
                  cursor: "not-allowed",
                }}
              >
                Edit
              </CampaignDetailLink>
            </HStack>
            <Text
              fontSize="xs"
              color="gray.700"
              fontFamily="Work Sans"
              fontWeight="400"
              lineHeight="140%"
            >
              {primaryText}
            </Text>
          </VStack>
        </>
      )}

      {hasIdentity && (
        <>
          <Divider borderColor="gray.200" />
          <HStack gap={4} align="center" w="full" px={4} py={2}>
            {instagramActorId && (
              <HStack gap={2} align="center" flex={1} minW={0}>
                <Icon
                  as={FaInstagram}
                  boxSize={4}
                  color="gray.600"
                  flexShrink={0}
                />
                <Text
                  fontSize="xs"
                  color="gray.600"
                  fontFamily="Work Sans"
                  fontWeight="500"
                  lineHeight="130%"
                  noOfLines={1}
                >
                  {instagramDisplayLabel ?? "—"}
                </Text>
              </HStack>
            )}
            {facebookPageId && (
              <HStack gap={2} align="center" flex={1} minW={0}>
                <Icon
                  as={FaFacebook}
                  borderRadius="full"
                  boxSize={4}
                  color="gray.600"
                  flexShrink={0}
                />
                <Text
                  fontSize="xs"
                  color="gray.600"
                  fontFamily="Work Sans"
                  fontWeight="500"
                  lineHeight="130%"
                  noOfLines={1}
                >
                  {facebookPageName ?? "—"}
                </Text>
              </HStack>
            )}
          </HStack>
        </>
      )}
    </VStack>
  );
}
